import { Button, Flex, Modal, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import React, { type Key, type ReactNode, useEffect, useReducer, useState } from 'react';
import { from, switchMap, throwError, of, tap, catchError, EMPTY } from 'rxjs';
import FoodGroupForm from './food-group.form';
import FoodGroupsFilter, { type FoodGroupsFiltersProps } from './food-groups.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { FoodGroup } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'FoodGroup deleted successfully';

interface FoodGroupsPageState {
  filters?: FoodGroupsFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const serializeSort = (sort: FoodGroupsPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: FoodGroupsPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: FoodGroupsPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | FoodGroupsPageState['filters']
      | FoodGroupsPageState['pagination']
      | FoodGroupsPageState['sort'];
  }
): FoodGroupsPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as FoodGroupsPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as FoodGroupsPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as FoodGroupsPageState['sort'],
      };
  }
};

const FoodGroupsPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [foodGroupToEdit, setFoodGroupToEdit] = useState<FoodGroup>();
  const foodGroups = useQuery<FoodGroup[]>(
    debouncedState && [
      'food-group',
      {
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  useEffect(() => {
    foodGroups.error &&
      notification.error({
        message: ' ',
        description: foodGroups.error.detail,
      });
  }, [foodGroups.error]);

  const columns: DataTableProps<FoodGroup>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setFoodGroupToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
    },
  ];

  return (
    <Flex gap="middle" vertical>
      <DataTable<FoodGroup>
        title="Food groups"
        description="List of food groups ingredients can be categorized by"
        filtersNode={
          <FoodGroupsFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setFoodGroupToEdit(new FoodGroup());
              }}
            >
              Add Food Group
            </Button>
          </AuthorizedComponent>
        }
        dataList={foodGroups}
        onChange={setState}
        currentPage={state.pagination.current}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(foodGroups.client.delete(['food-group', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({ message: ' ', description: SUCCESS_DELETE_MESSAGE });
                        foodGroups.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete food group',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />
      <SyncSuspense condition={foodGroupToEdit}>
        <ExtraContent>
          <FoodGroupForm
            foodGroup={foodGroupToEdit ?? new FoodGroup()}
            onSuccess={(_foodgroup) => {
              foodGroups.refetch?.();
              setFoodGroupToEdit(undefined);
            }}
            onCancel={() => {
              setFoodGroupToEdit(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default FoodGroupsPage;
